import type { FileRoutesByTo } from '@/routeTree.gen';

/** A route as a link's `to` spells it. */
export type RouteTo = keyof FileRoutesByTo;

/** A route of the new tree. Only these are a moved page's new route, and none is an old one, so no reroute loops. */
type NewRouteTo = Extract<RouteTo, `/next/${string}`>;

type ParamsOf<TTo extends RouteTo> = FileRoutesByTo[TTo]['types']['allParams'];

/**
 * Whether two routes take the same params: the same names, each required or optional in both. The names are compared
 * too, since an optional param is assignable to none at all. Exported for the type tests.
 */
export type SameParams<TFrom, TTo> = [keyof TFrom, keyof TTo, TFrom, TTo] extends [keyof TTo, keyof TFrom, TTo, TFrom]
    ? true
    : false;

/** A page the new navigation has moved: its route in today's tree, and its route below `/next`. */
export type MovedPage = {
    from: Exclude<RouteTo, '/next' | NewRouteTo>;
    to: NewRouteTo;
};

/**
 * Checks the page's two routes against the route tree. Both take the same params, so the reroute passes a page's
 * params on as they are.
 */
export function movedPage<TFrom extends MovedPage['from'], TTo extends NewRouteTo>(page: {
    from: TFrom;
    to: SameParams<ParamsOf<TFrom>, ParamsOf<TTo>> extends true
        ? TTo
        : "The new route takes the same params as today's";
}): MovedPage {
    return page as MovedPage;
}
